import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import type { SorterResult } from "antd/es/table/interface";
import orderBy from "lodash/orderBy";
import { useDispatch } from "react-redux";

import { type ActionButton, Table } from "@similarweb/pro-logic-components";

import { showErrorToast, showSuccessToast } from "app/actions/toast_actions";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { SharingAssetModal } from "app/components/sharingAssetModal/SharingAssetModal";
import { i18nFilter } from "app/filters/i18nFilter";
import { DefaultFetchService } from "app/services/fetchService";
import type { ISegmentsApiService } from "app/services/segments/segmentsApiService";
import SegmentsApiService from "app/services/segments/segmentsApiService";
import { getHasSegmentShareClaim } from "app/services/segments/SegmentsUtils";
import { allTrackers } from "app/services/track/track";
import ShareExternalDialog from "pro-features/components/ShareExternalDialog/ShareExternalDialog";
import type { IUser } from "pro-features/components/ShareExternalDialog/ShareExternalDialog";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { getTabNameForTracking } from "../../features/segments-homepage/SegmentTableTabs";
import { resolveTrackingCustomAttributesBase } from "../../redesigned-wizard/segment-wizard/utils/resolveTrackingCustomAttributes";
import { ROWS_PER_PAGE } from "../constants/common";
import { segmentsTableOptions } from "../CustomSegmentsTableConfig";
import { getModalText } from "../sharingModalCopy";
import { StyledTableWrapper } from "../StyledComponents";

import { BulkShareSegmentsModal } from "./BulkShareSegmentsModal";
import { DeleteSegmentsModal } from "./DeleteSegmentModal";
import { SegmentGroupSelectionPopover } from "./SegmentGroupSelectionPopover";
import { useWebsiteSegmentsTableSelection } from "./useWebsiteSegmentsTableSelection";
import { getWebsiteSegmentsColumns } from "./WebsiteSegmentsTableColumns";
import type {
    IWebsiteSegmentsTablePropsWithConfig,
    TableSelectionKey,
    WebsiteSegmentRow,
} from "./WebsiteSegmentsTableTypes";

const SEGMENT_SHARE_API_ACTION = "DuplicateSegmentsToOthers";

type SortOrder = "ascend" | "descend";
type SortConfig = {
    field: string;
    order: SortOrder;
};

const DEFAULT_SORT: SortConfig = {
    field: "lastUpdated",
    order: "descend",
};
const getTableAutomationValue = (tableSelectionKey: TableSelectionKey): string => {
    return tableSelectionKey === "MySegmentsTable"
        ? "segments-website-table-my-segments"
        : "segments-website-table-org-segments";
};

const resolveSafePage = (currentPage: number, totalRows: number, rowsPerPage: number): number => {
    const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

    if (currentPage < 1) {
        return 1;
    }

    return Math.min(currentPage, totalPages);
};

const filterWebsiteSegmentsTableData = (tableData: WebsiteSegmentRow[], query: string): WebsiteSegmentRow[] => {
    if (!query) {
        return tableData;
    }

    const normalizedQuery = query.toLowerCase();
    return tableData.filter(({ domain, segmentName, userName }) => {
        const normalizedUserName = typeof userName === "string" ? userName.toLowerCase() : "";
        return (
            domain?.toLowerCase().includes(normalizedQuery) ||
            segmentName?.toLowerCase().includes(normalizedQuery) ||
            normalizedUserName.includes(normalizedQuery)
        );
    });
};

const getSelectionText = (count: number, t: (key: string, values?: Record<string, unknown>) => string): string => {
    const textKey = count === 1 ? "table.selection.segment.selected" : "table.selection.segments.selected";
    return t(textKey, { count });
};

export const WebsiteSegmentsTable = ({
    isLoading = false,
    tableData = [],
    isLimitationExceeded,
    tableSelectionKey,
    showOwnerColumn,
    noData,
    hasOwnerActions = false,
    withComparisonSelection = false,
}: IWebsiteSegmentsTablePropsWithConfig) => {
    const dispatch = useDispatch();
    const t = useTranslation();
    const { params } = useCurrentStateAndParams();
    const segmentsApiService: ISegmentsApiService = useMemo(() => new SegmentsApiService(), []);
    const currentModule = proNavigator.getCurrentModule();
    const isSegmentSharingEnabled = getHasSegmentShareClaim();

    const [sortConfig, setSortConfig] = useState<SortConfig>(DEFAULT_SORT);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const [externalDialogIsOpen, setExternalDialogIsOpen] = useState(false);
    const [segmentForShare, setSegmentForShare] = useState<WebsiteSegmentRow | null>(null);
    const [isShareModalOpen, setShareModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isBulkShareModalOpen, setIsBulkShareModalOpen] = useState(false);
    const [isComparisonPopoverOpen, setIsComparisonPopoverOpen] = useState(false);
    const comparisonButtonRef = useRef<HTMLButtonElement>(null);

    const filteredData = useMemo(
        () => filterWebsiteSegmentsTableData(tableData, searchQuery),
        [tableData, searchQuery],
    );
    const sortedData = useMemo(() => {
        return orderBy(filteredData, sortConfig.field, sortConfig.order === "ascend" ? "asc" : "desc");
    }, [filteredData, sortConfig.field, sortConfig.order]);
    const currentPageRows = useMemo(() => {
        const pageStartIndex = (currentPage - 1) * ROWS_PER_PAGE;
        return sortedData.slice(pageStartIndex, pageStartIndex + ROWS_PER_PAGE);
    }, [currentPage, sortedData]);

    const {
        selectedRows,
        selectedRowKeys,
        selectedRowsCount,
        clearAllSelectedRows,
        selectionProps,
        topSelectionDropdownProps,
    } = useWebsiteSegmentsTableSelection({
        sortedData,
        currentPageRows,
        currentPage,
        hasOwnerActions,
        translate: t,
    });

    useEffect(() => {
        const nextPage = resolveSafePage(currentPage, sortedData.length, ROWS_PER_PAGE);
        if (nextPage !== currentPage) {
            setCurrentPage(nextPage);
        }
    }, [currentPage, sortedData.length]);

    const showToastMessage = useCallback(
        (href: string | null, text: string, label: string | null) => {
            dispatch(
                showSuccessToast(
                    getToastItemComponent({
                        text,
                        linkText: label,
                        href,
                        onClick: () =>
                            allTrackers.dispatchTrackEvent("create segments group", "click", "internal link"),
                    }),
                ),
            );
        },
        [dispatch],
    );

    const showErrorToastMessage = useCallback(
        (text: string) => {
            dispatch(showErrorToast(text));
        },
        [dispatch],
    );

    const onChangeExternalModalState = useCallback((isOpen: boolean) => {
        setExternalDialogIsOpen(isOpen);
    }, []);

    const onPickSegment = useCallback((segment: WebsiteSegmentRow) => {
        setSegmentForShare(segment);
    }, []);

    const dispatchSuccessToast = useCallback((text: string) => showToastMessage(null, text, null), [showToastMessage]);
    const dispatchErrorToast = useCallback((text: string) => showErrorToastMessage(text), [showErrorToastMessage]);

    const onShareSubmit = useCallback(
        async (sharing) => {
            if (!segmentForShare) {
                return;
            }
            await segmentsApiService.shareSegment(segmentForShare, sharing);
            dispatchSuccessToast(i18nFilter()("segment.sharing.success.toast"));
            setShareModalOpen(false);
        },
        [dispatchSuccessToast, segmentForShare, segmentsApiService],
    );

    const onClickUserExternalDialog = useCallback(
        async (user: IUser) => {
            if (!segmentForShare) {
                return false;
            }

            const customAttributes = resolveTrackingCustomAttributesBase(segmentForShare);
            const selectedSegmentTab = (params as { tab?: string })?.tab;
            allTrackers.dispatchTrackEvent("Button", "click", "Copy to user/done", {
                ...customAttributes,
                tab: getTabNameForTracking({ selectedSegmentTab }),
                /* eslint-disable-next-line @typescript-eslint/naming-convention -- tracking API requires snake_case */
                target_user_email: user.UserName,
            });

            const fetchService = DefaultFetchService.getInstance();
            /* eslint-disable @typescript-eslint/naming-convention -- API requires PascalCase field names */
            const reportData = {
                UserId: user.Id,
                SegmentId: segmentForShare.id,
                UserAccountId: user.AccountId,
            };
            /* eslint-enable @typescript-eslint/naming-convention */

            try {
                await fetchService.post("/api/userdata/segments/duplicateTo", reportData);
                onChangeExternalModalState(false);
                void segmentsApiService.refreshSegmentsMetaData();

                return true;
            } catch {
                return false;
            }
        },
        [onChangeExternalModalState, params, segmentForShare, segmentsApiService],
    );

    const handleToastMessage = useCallback(
        (user: IUser, isSucceeded: boolean) => {
            const successMessage = t("segment.share.external.modal.success.message", {
                segmentName: segmentForShare?.segmentName,
                userName: user?.UserName,
            });
            const failMessage = t("segment.share.external.modal.fail.message");

            return isSucceeded ? successMessage : failMessage;
        },
        [segmentForShare?.segmentName, t],
    );

    const tableOptions = useMemo(
        () => segmentsTableOptions(setShareModalOpen, onChangeExternalModalState, onPickSegment, isLimitationExceeded),
        [isLimitationExceeded, onChangeExternalModalState, onPickSegment],
    );

    const columns = useMemo(
        () =>
            getWebsiteSegmentsColumns({
                currentPage,
                totalCount: sortedData.length,
                sortedField: sortConfig.field,
                sortOrder: sortConfig.order,
                currentModule,
                rowActionRenderers: tableOptions.rowActionsComponents ?? [],
                translate: t,
                showOwnerColumn,
            }),
        [
            currentModule,
            currentPage,
            showOwnerColumn,
            sortConfig.field,
            sortConfig.order,
            sortedData.length,
            tableOptions.rowActionsComponents,
            t,
        ],
    );

    const handleSearchChange = useCallback((nextQuery: string) => {
        setSearchQuery(nextQuery);
        setCurrentPage(1);
    }, []);

    const handleSort = useCallback((sorter: SorterResult<WebsiteSegmentRow>) => {
        if (!sorter?.columnKey || !sorter?.order) {
            return;
        }
        setSortConfig({
            field: String(sorter.columnKey),
            order: sorter.order as SortOrder,
        });
        setCurrentPage(1);
    }, []);

    const handlePaginationChange = useCallback((_prevPage: number, _pageSize: number, nextPage: number) => {
        setCurrentPage(nextPage);
    }, []);

    const handleOpenBulkShareModal = useCallback(() => {
        setIsBulkShareModalOpen(true);
        allTrackers.dispatchTrackEvent("bulk share segments", "open", "bulk share modal", {
            segmentCount: selectedRowsCount,
        });
    }, [selectedRowsCount]);

    const handleBulkShareSuccess = useCallback(() => {
        dispatchSuccessToast(t("segments.bulk.share.success.toast"));
        clearAllSelectedRows();
        setIsBulkShareModalOpen(false);
    }, [clearAllSelectedRows, dispatchSuccessToast, t]);

    const handleBulkUnshareSuccess = useCallback(() => {
        dispatchSuccessToast(t("segment.sharing.success.toast"));
        clearAllSelectedRows();
        setIsBulkShareModalOpen(false);
    }, [clearAllSelectedRows, dispatchSuccessToast, t]);

    const handleBulkShareError = useCallback(() => {
        dispatchErrorToast(t("segments.bulk.share.fail.toast"));
    }, [dispatchErrorToast, t]);

    const handleDelete = useCallback(() => {
        segmentsApiService
            .deleteCustomSegments(selectedRows)
            .then(() => {
                dispatchSuccessToast(t("segments.analysis.table.multi.delete.success.toast"));
                allTrackers.dispatchTrackEvent("delete", "click", "delete segments", {
                    deletedSegments: selectedRowsCount,
                });
            })
            .catch(() => {
                dispatchErrorToast("segments.analysis.table.multi.delete.failure.toast");
            })
            .finally(() => {
                setIsConfirmModalOpen(false);
                clearAllSelectedRows();
            });
    }, [
        clearAllSelectedRows,
        dispatchErrorToast,
        dispatchSuccessToast,
        selectedRows,
        selectedRowsCount,
        segmentsApiService,
        t,
    ]);

    const selectionActionButtons = useMemo(() => {
        const buttons: ActionButton[] = [];

        if (withComparisonSelection) {
            buttons.push({
                id: "select-list",
                iconName: "chev-down",
                children: t("table.selection.keywords.selectgroup"),
                onClick: () => setIsComparisonPopoverOpen(true),
                ref: comparisonButtonRef,
            });
        }

        if (hasOwnerActions) {
            if (isSegmentSharingEnabled) {
                buttons.push({
                    id: "share-selected-segments",
                    iconName: "share-new",
                    children: t("common.share"),
                    onClick: handleOpenBulkShareModal,
                });
            }
            buttons.push({
                id: "delete-selected-segments",
                iconName: "delete",
                children: t("common.delete"),
                onClick: () => setIsConfirmModalOpen(true),
            });
        }

        return buttons;
    }, [handleOpenBulkShareModal, t, hasOwnerActions, isSegmentSharingEnabled, withComparisonSelection]);

    const topSelectionProps = useMemo(
        () => ({
            hideCount: true,
            selectText: getSelectionText(selectedRowsCount, t),
            actionButtons: selectionActionButtons,
            selectAllDropdown: topSelectionDropdownProps,
        }),
        [selectedRowsCount, selectionActionButtons, t, topSelectionDropdownProps],
    );

    const noDataConfig = {
        title: noData.titleKey ? t(noData.titleKey) : undefined,
        subtitle: noData.subtitleKey ? t(noData.subtitleKey) : undefined,
        showHeaderWhenEmpty: true,
    };

    return (
        <StyledTableWrapper
            loading={isLoading}
            data-automation={getTableAutomationValue(tableSelectionKey)}
        >
            {hasOwnerActions && isConfirmModalOpen ? (
                <DeleteSegmentsModal
                    isOpen={isConfirmModalOpen}
                    setIsOpen={setIsConfirmModalOpen}
                    onDelete={handleDelete}
                    selectedSegments={selectedRows}
                />
            ) : null}
            <ShareExternalDialog
                dialogIsOpen={externalDialogIsOpen}
                setDialogIsOpen={onChangeExternalModalState}
                onClickUser={onClickUserExternalDialog}
                generateToastMessage={handleToastMessage}
                titleText={t("Usermanagement.Modal.share.segment.Title")}
                usersApiAction={SEGMENT_SHARE_API_ACTION}
            />
            {segmentForShare ? (
                <SharingAssetModal
                    onSubmit={onShareSubmit}
                    modalText={getModalText(segmentForShare)}
                    sharedWithUsers={segmentForShare.sharedWithUsers}
                    initialSharingState={segmentsApiService.resolveSharingStateToShareModalFormat(segmentForShare)}
                    isOpen={isShareModalOpen}
                    setIsOpen={setShareModalOpen}
                />
            ) : null}
            {hasOwnerActions ? (
                <BulkShareSegmentsModal
                    isOpen={isBulkShareModalOpen}
                    setIsOpen={setIsBulkShareModalOpen}
                    selectedSegments={selectedRows}
                    onShareSuccess={handleBulkShareSuccess}
                    onUnshareSuccess={handleBulkUnshareSuccess}
                    onError={handleBulkShareError}
                />
            ) : null}
            {withComparisonSelection ? (
                <SegmentGroupSelectionPopover
                    triggerRef={comparisonButtonRef}
                    open={isComparisonPopoverOpen}
                    onOpenChange={setIsComparisonPopoverOpen}
                    selectedSegmentIds={selectedRowKeys}
                    clearAllSelectedRows={clearAllSelectedRows}
                    showToast={showToastMessage}
                />
            ) : null}
            <Table<WebsiteSegmentRow>
                rowKey="id"
                data={currentPageRows}
                columnConfig={columns}
                isLoading={isLoading}
                loaderRowCount={5}
                withArrowButtons={false}
                theme="gray"
                onSort={handleSort}
                pagination={{
                    total: sortedData.length,
                    pageSize: ROWS_PER_PAGE,
                    current: currentPage,
                    onChange: handlePaginationChange,
                }}
                noData={noDataConfig}
                selection={selectionProps}
            >
                <Table.TableTop>
                    <Table.TableTop.Filters
                        filters={[
                            {
                                filter: "search",
                                placeholder: t("common.search.placeholder"),
                                onSearchChange: handleSearchChange,
                                initialValue: searchQuery,
                            },
                        ]}
                    />
                    <Table.TableTop.Selection {...topSelectionProps} />
                </Table.TableTop>
            </Table>
        </StyledTableWrapper>
    );
};
